// App.tsx — top-level URL routing.
//
// Route map (when setup is NOT required):
//   /                                        → if authenticated, redirect to /chooser; else /login
//   /login                                   → LoginScreen
//   /setup                                   → redirect to /login (setup already done)
//   /chooser                                 → Chooser (post-login first stop)            [auth-gated]
//   /browse                                  → BrowseView, first workspace selected      [auth-gated]
//   /browse/:teamId                          → BrowseView, that workspace, no convo      [auth-gated]
//   /browse/:teamId/:conversationId          → BrowseView, workspace + conversation      [auth-gated]
//   /browse/:teamId/:conversationId?ts=…     → conversation scrolled to that ts          [auth-gated]
//   /status                                  → StatusPage (system diagnostics list)      [auth-gated]
//   /status/:teamId                          → StatusDetailPage (one workspace)          [auth-gated]
//   *                                        → 404 → redirect to /
//
// Pass 6.0c.2: the /browse routes embed workspace + conversation
// as URL params so refresh (any kind) restores the view, links are
// shareable, and back/forward navigation works. BrowseView reads
// the params and treats the URL as the source of truth — clicking
// a workspace rail item or conversation list entry navigate()s
// the URL; React state derives from it on every render.
//
// Route map (when setup IS required, Phase 6.0a.1):
//   /setup                 → SetupPage (the wizard form)
//   *                      → redirect to /setup
//
// The auth-gated routes are wrapped in <RequireAuth>, which
// handles the three-state machine (loading → splash,
// unauthenticated → /login, authenticated → render children).
// They're also wrapped in <SystemStatusProvider> (Phase 5.0),
// which polls /system/status every 60s; placing the provider
// INSIDE the auth gate means the poll never fires while the
// user is unauthenticated (otherwise every tick would 401 and
// trigger the auto-logout handler).
//
// Pass 3.8 cutover: the /upload route + UploadView are gone with
// the rest of the ZIP path. Browse is the only auth-gated
// destination beyond the post-login Chooser. Phase 5.0 adds
// /status as a second non-Browse auth-gated route. Phase 6.0a.1
// adds the top-level setup-required gate.

import { useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { BrowseView } from "./views/BrowseView";
import { StatusPage } from "./views/StatusPage";
import { StatusDetailPage } from "./views/StatusDetailPage";
import { SetupPage } from "./views/SetupPage";
import { UsersPage } from "./views/UsersPage";
import { NotFoundPage } from "./views/NotFoundPage";
import { Chooser } from "./components/Chooser";
import { AmbientBackground } from "./components/AmbientBackground";
import { LoginScreen } from "./components/LoginScreen";
import { RequireAuth } from "./components/RequireAuth";
import { SplashScreen } from "./components/SplashScreen";
import { useAuth } from "./state/AuthContext";
import { useImpersonation } from "./state/ImpersonationContext";
import {
  SetupStatusProvider,
  useSetupStatus,
} from "./state/SetupStatusContext";
import { SystemStatusProvider } from "./state/SystemStatusContext";

/** Phase 6.2: wrap an auth-gated, data-scoped page. Keying the
 *  SystemStatusProvider by the impersonation scope remounts the
 *  whole subtree (page + its data hooks + the status poll) whenever
 *  the superadmin switches "Viewing:" — the no-React-Query way to
 *  invalidate every scoped query at once. Navigation within a scope
 *  keeps the key stable, so it does NOT remount on route param
 *  changes (e.g. picking a different workspace/conversation). */
function Scoped({ children }: { children: ReactNode }) {
  const { scopeKey } = useImpersonation();
  return (
    <RequireAuth>
      <SystemStatusProvider key={scopeKey}>{children}</SystemStatusProvider>
    </RequireAuth>
  );
}

export default function App() {
  // The setup-status provider wraps the entire Routes tree so
  // RoutesGate inside can switch on setupRequired without
  // re-mounting any state. The fetch happens ONCE on mount.
  return (
    <SetupStatusProvider>
      {/* Phase 7.0 S1 — ambient background foundation, behind all
          content. Page roots are transparent so it shows through. */}
      <AmbientBackground />
      <RoutesGate />
    </SetupStatusProvider>
  );
}

/** Phase 6.0a.1 top-level gate. Routes the user to the setup
 *  wizard when sync_users is empty; otherwise serves the normal
 *  Routes tree. During the initial /setup/status fetch we render
 *  the splash so the very first paint isn't a confusing partial
 *  state. */
function RoutesGate() {
  const { loading, setupRequired } = useSetupStatus();

  if (loading) {
    return <SplashScreen />;
  }

  if (setupRequired) {
    // Everything redirects to /setup until the wizard completes.
    // The redirect chain unblocks itself the moment SetupPage
    // calls markSetupDone() (POST /setup success).
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<LoginGate />} />
      {/* /setup is a dead end once setup is done — bounce to /login
          so a curious operator typing the URL doesn't see a stale
          wizard. */}
      <Route path="/setup" element={<Navigate to="/login" replace />} />
      <Route
        path="/chooser"
        element={
          <RequireAuth>
            <SystemStatusProvider>
              <Chooser />
            </SystemStatusProvider>
          </RequireAuth>
        }
      />
      {/* Pass 6.0c.2: nested forms all render the same BrowseView;
          BrowseView reads :teamId / :conversationId via useParams
          and ?ts= via useSearchParams. The bare /browse form
          preserves the pre-6.0c.2 "land on first workspace"
          default. */}
      <Route
        path="/browse"
        element={
          <Scoped>
            <BrowseView />
          </Scoped>
        }
      />
      <Route
        path="/browse/:teamId"
        element={
          <Scoped>
            <BrowseView />
          </Scoped>
        }
      />
      <Route
        path="/browse/:teamId/:conversationId"
        element={
          <Scoped>
            <BrowseView />
          </Scoped>
        }
      />
      <Route
        path="/status"
        element={
          <Scoped>
            <StatusPage />
          </Scoped>
        }
      />
      <Route
        path="/status/:teamId"
        element={
          <Scoped>
            <StatusDetailPage />
          </Scoped>
        }
      />
      {/* Phase 6.1: user management. RequireAuth gates auth;
          UsersPage itself redirects non-superadmin to /chooser
          (the server also re-checks role on every /users call). */}
      <Route
        path="/users"
        element={
          <RequireAuth>
            <UsersPage />
          </RequireAuth>
        }
      />
      {/* Phase 6.3.2 (A.4): real 404 page instead of a silent
          redirect to "/", so a mistyped URL is visibly wrong. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/** Root path: route based on auth status. */
function RootRedirect() {
  const { status } = useAuth();
  if (status === "loading") {
    // Splash during initial token validation. Without this guard
    // the redirect would fire BEFORE validation completes and
    // we'd send authenticated users to /login on every reload.
    return <SplashScreen />;
  }
  return <Navigate to={status === "authenticated" ? "/chooser" : "/login"} replace />;
}

/** /login: if already authenticated, replace this entry with
 *  /chooser so the browser's Back button doesn't take the user
 *  back to /login from /chooser (Pass 2.7). */
export function LoginGate() {
  const { status } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "authenticated") {
      navigate("/chooser", { replace: true });
    }
  }, [status, navigate]);

  if (status === "loading") {
    return <SplashScreen />;
  }
  if (status === "authenticated") {
    return null;
  }
  return <LoginScreen />;
}
